import { useMemo, ReactNode, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Popover } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RTTable from '../RTTable'
import { IHIDUser } from '../../graphql/hid/queryUser'
import { OrigoEventName } from '../../graphql/hid/getOrigoAccount'
import {
  handleStatus,
  getStatusColor,
  handleStatusDescription,
} from '../../utils/handleMobileCardStatus'
import { Tooltip } from '@mui/material'
import DeleteSelectedHIDCards from './DeleteSelectedHIDCards'
import { colors } from '../../const/colors'
import {
  NameCell,
  MutedCell,
  ServiceCell,
  BuildingCell,
} from '../Table/TableCells'
import { BulkActionsBar } from '../Table/BulkActionsBar'

const STATUS_KEYS = [
  'NEW',
  'INVITATION_PENDING',
  'ENDPOINT_ACTIVE',
  'CREDENTIAL_ISSUED',
  'ACTIVE',
  'CREDENTIAL_REVOKING',
  'CREDENTIAL_REVOKED',
] as const

const StatusColumnHeader = () => {
  const { t } = useTranslation(['hid', 'hid_status'])
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  return (
    <>
      <Box
        component="span"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
      >
        {t('hid:card_status')}
        <InfoOutlinedIcon
          onClick={e => {
            e.stopPropagation()
            setAnchor(e.currentTarget as unknown as HTMLElement)
          }}
          sx={{
            fontSize: 14,
            color: colors.slate,
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': { color: colors.navy },
          }}
        />
      </Box>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '14px',
              boxShadow: colors.shadowCard,
              border: `1px solid ${colors.border}`,
              p: '14px 18px',
              maxWidth: 380,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {STATUS_KEYS.map(key => (
            <Box key={key} sx={{ display: 'flex', gap: '10px' }}>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: colors.navy,
                  minWidth: 160,
                  flexShrink: 0,
                }}
              >
                {t(`hid_status:${key}`)}:
              </Typography>
              <Typography sx={{ fontSize: '13px', color: colors.textMuted }}>
                {t(`hid_status:${key}_DESC`)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  )
}

interface ITableColumns {
  Header: string | (() => JSX.Element)
  accessor:
    | 'fullName'
    | 'login'
    | 'workEmail'
    | 'tenantName'
    | 'hidAccount'
    | 'status'
    | 'cardNumber'
}

export interface IUserDataRow {
  fullName: string
  firstName: string
  lastName: string
  login: string
  workEmail: string
  tenantName: string
  tenantId: string
  tenantSlug: string
  hidAccount: boolean
  userId: string
  accountStatus?: OrigoEventName
  status: string
  cardNumber: string | number
  accessGranted: boolean
  deviceId: string
}

const HIDUsersTable = ({
  children,
  users,
  setOpenUserDetails,
  refetch,
}: {
  children: ReactNode
  users: IHIDUser[]
  setOpenUserDetails: (v: IUserDataRow | undefined) => void
  refetch: () => void
}) => {
  const { t } = useTranslation(['hid'])
  const [selected, setSelected] = useState<IUserDataRow[]>([])
  const [deleteOpen, setDeleteOpen] = useState(false)

  const handleSelectRows = useCallback((selectedRowsData: IUserDataRow[]) => {
    setSelected(selectedRowsData.filter(({ deviceId }) => !!deviceId))
  }, [])

  const tableColumns: ITableColumns[] = useMemo(
    () => [
      {
        Header: t('columns:full_name'),
        accessor: 'fullName',
        Cell: ({ value }: { value: string }) => <NameCell value={value} />,
      },
      {
        Header: t('columns:login'),
        accessor: 'login',
        Cell: ({ value }: { value: string }) => <MutedCell value={value} />,
      },
      {
        Header: t('columns:work_email'),
        accessor: 'workEmail',
        Cell: ({ value }: { value: string }) => <MutedCell value={value} />,
      },
      {
        Header: t('columns:company'),
        accessor: 'tenantName',
        Cell: ({ value }: { value: string }) => <BuildingCell value={value} />,
      },
      {
        Header: t('mobile_card_activity'),
        accessor: 'hidAccount',
        Cell: ({ value }: { value: boolean }) => (
          <ServiceCell
            value={value ? t('bool:yes') : t('bool:no')}
            yesLabel={t('bool:yes')}
          />
        ),
      },
      {
        Header: () => <StatusColumnHeader />,
        accessor: 'status',
        Cell: (props: any) =>
          props.value ? (
            <Tooltip
              title={handleStatusDescription(
                props.value,
                props.row.original.accessGranted,
              )}
              arrow
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background:
                      getStatusColor(
                        props.value,
                        props.row.original.accessGranted,
                      ) || colors.slate,
                    flexShrink: 0,
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    fontSize: '13.5px',
                    fontWeight: 700,
                    color:
                      getStatusColor(
                        props.value,
                        props.row.original.accessGranted,
                      ) || colors.textBody,
                  }}
                >
                  {handleStatus(props.value, props.row.original.accessGranted)}
                </Box>
              </Box>
            </Tooltip>
          ) : null,
      },
      {
        Header: t('card_number'),
        accessor: 'cardNumber',
        Cell: ({ value }: { value: string | number }) => (
          <Box
            component="span"
            sx={{
              fontFamily: 'monospace',
              fontSize: '13px',
              color: colors.textMuted,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value || '—'}
          </Box>
        ),
      },
    ],
    [t],
  )

  const tableData: IUserDataRow[] = useMemo(
    () =>
      users.map(
        ({
          login,
          firstName,
          lastName,
          email,
          workEmail,
          tenant,
          origoAccounts,
        }) => ({
          login,
          firstName,
          lastName,
          fullName: `${firstName} ${lastName}`,
          workEmail: workEmail?.email ? workEmail.email : email.email,
          tenantName: tenant.name || '',
          tenantId: tenant.id || '',
          tenantSlug: tenant.slug,
          hidAccount: !!origoAccounts[0]?.origoCards[0]?.deviceId,
          userId: !!origoAccounts.length ? origoAccounts[0].userId : '',
          accountStatus: origoAccounts[0]?.status,
          status: origoAccounts[0]?.origoCards[0]?.status || '',
          cardNumber: origoAccounts[0]?.origoCards[0]?.cardNumber || '',
          accessGranted: !!origoAccounts[0]?.origoCards[0]?.accessGranted,
          deviceId: origoAccounts[0]?.origoCards[0]?.deviceId || '',
        }),
      ) || [],
    [users],
  )

  return (
    <Box>
      {selected.length > 0 && (
        <BulkActionsBar
          count={selected.length}
          onClear={() => setSelected([])}
          actions={[
            {
              label: t('delete_HID_cards'),
              onClick: () => setDeleteOpen(true),
              variant: 'danger',
              icon: <DeleteOutlineIcon sx={{ fontSize: 15 }} />,
            },
          ]}
        />
      )}

      <RTTable
        columns={tableColumns}
        data={tableData}
        onRowClick={rowData => setOpenUserDetails(rowData)}
        pagination
        orderByProps="fullName"
        sortDesc={false}
        rowSelect
        csvExport
        onSelectionChange={handleSelectRows}
        toolbarSlot={children}
      />

      <DeleteSelectedHIDCards
        items={selected}
        refetch={refetch}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
      />
    </Box>
  )
}

export default HIDUsersTable
